import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, Droplet, Wind, TrendingUp, Layers, ChevronLeft, ChevronRight, Fish, Lightbulb, Ruler } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useCart from '@/store/cartStore';
import { toast } from 'sonner';
import { LiveArrivalBadge, ShippingCountdown, LowStockBadge, EstimatedDelivery } from '@/components/ShippingFeatures';
import ProductReviews from '@/components/ProductReviews';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const addItem = useCart((state) => state.addItem);
  
  // Bundle form state
  const [bundleForm, setBundleForm] = useState({
    tank_size: '',
    lighting_level: '',
    tank_inhabitants: '',
    additional_notes: ''
  });
  const [bundleFormError, setBundleFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    // Check if bundle product requires form
    const isBundle = product.category === 'bundles';
    
    if (isBundle) {
      // Validate required bundle fields
      if (!bundleForm.tank_size || !bundleForm.lighting_level || !bundleForm.tank_inhabitants) {
        setBundleFormError('Please fill out all required fields for your custom bundle');
        toast.error('Please complete the tank information form');
        return;
      }
      setBundleFormError('');
    }
    
    const bundleData = isBundle ? bundleForm : null;
    const success = await addItem({ ...product, id: product._id }, quantity, bundleData);
    
    if (success) {
      toast.success(`${product.name} added to cart!`, { duration: 1000 });
      // Reset bundle form after adding
      if (isBundle) {
        setBundleForm({ tank_size: '', lighting_level: '', tank_inhabitants: '', additional_notes: '' });
      }
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  const getAllImages = () => {
    if (!product) return [];
    const images = [];
    
    if (product.image_url) {
      images.push({ url: product.image_url, alt_text: product.name });
    }
    
    if (product.image_gallery && Array.isArray(product.image_gallery)) {
      product.image_gallery.forEach(img => {
        if (img.url && img.url !== product.image_url) {
          images.push(img);
        }
      });
    }
    
    return images;
  };

  const images = getAllImages();
  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <Link to="/shop">
            <Button>Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  const displayPrice = product.sale_price || product.price;
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  
  const backLink = product.category ? `/shop?category=${product.category}` : '/shop';
  const backText = product.category 
    ? `Back to ${product.category.charAt(0).toUpperCase() + product.category.slice(1)}` 
    : 'Back to Shop';

  return (
    <>
      <div className="min-h-screen py-12 md:py-16 pb-24 md:pb-16" data-testid="product-detail-page">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          <Link to={backLink} className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8" data-testid="back-to-shop">
            <ArrowLeft className="w-4 h-4" />
            {backText}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image Gallery Section */}
            <div className="space-y-4">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-secondary/30" data-testid="product-image">
                <img
                  src={images[selectedImageIndex]?.url || product.image_url}
                  alt={images[selectedImageIndex]?.alt_text || product.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Low Stock Badge on Image */}
                {product.inventory_quantity > 0 && product.inventory_quantity <= 5 && (
                  <div className="absolute top-3 right-3">
                    <LowStockBadge quantity={product.inventory_quantity} />
                  </div>
                )}
                
                {hasMultipleImages && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    
                    <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                      {selectedImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {hasMultipleImages && (
                <div className="flex gap-2 overflow-x-auto pb-2" data-testid="image-thumbnails">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        index === selectedImageIndex 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'border-transparent hover:border-muted-foreground/30'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img
                        src={img.url}
                        alt={img.alt_text || `${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info Section */}
            <div>
              <p className="text-sm uppercase tracking-wider text-muted-foreground mb-2">{product.category}</p>
              <h1 className="heading-font text-3xl sm:text-4xl md:text-5xl font-bold mb-4" data-testid="product-detail-name">{product.name}</h1>
              <p className="text-base md:text-lg text-muted-foreground mb-6">{product.description}</p>

              {/* Live Arrival Guarantee Badge */}
              <LiveArrivalBadge variant="compact" className="mb-6" />

              {/* Price Section with Sale Indicators */}
              <div className="mb-6">
                {hasDiscount ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 md:p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-lg">
                        {Math.round((1 - product.sale_price / product.price) * 100)}% OFF SALE
                      </span>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl md:text-5xl font-bold text-red-500" data-testid="product-detail-price">
                        ${displayPrice.toFixed(2)}
                      </span>
                      <span className="text-xl md:text-2xl text-muted-foreground line-through decoration-2">
                        ${product.price.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-green-600 font-semibold mt-2">
                      You save ${(product.price - product.sale_price).toFixed(2)}!
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <span className="text-3xl md:text-4xl font-bold text-primary" data-testid="product-detail-price">
                      ${displayPrice.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Shipping Countdown Timer */}
              <ShippingCountdown className="mb-6" />

              {/* Estimated Delivery */}
              <EstimatedDelivery className="mb-6" />

              {product.care_guide && (
                <Card className="p-5 md:p-6 mb-6 overflow-hidden" data-testid="care-guide">
                  <h3 className="heading-font text-xl md:text-2xl font-semibold mb-4">Plant Care Guide</h3>
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <div className="flex items-start gap-2 md:gap-3 min-w-0">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Droplet className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs md:text-sm text-muted-foreground">Light Level</p>
                        <p className="font-semibold text-xs md:text-base truncate" data-testid="care-light">{product.care_guide.light}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 md:gap-3 min-w-0">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Wind className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs md:text-sm text-muted-foreground">CO2</p>
                        <p className="font-semibold text-xs md:text-base truncate" data-testid="care-co2">{product.care_guide.co2}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 md:gap-3 min-w-0">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs md:text-sm text-muted-foreground">Growth Rate</p>
                        <p className="font-semibold text-xs md:text-base truncate" data-testid="care-growth">{product.care_guide.growth_rate}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 md:gap-3 min-w-0">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Layers className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs md:text-sm text-muted-foreground">Placement</p>
                        <p className="font-semibold text-xs md:text-base truncate" data-testid="care-placement">{product.care_guide.placement}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm md:text-lg font-bold text-primary">!</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs md:text-sm text-muted-foreground">Difficulty Level</p>
                        <p className="font-semibold text-sm md:text-lg" data-testid="care-difficulty">{product.care_guide.difficulty}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2 bg-secondary rounded-full p-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 md:h-10 md:w-10 rounded-full"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="decrease-qty"
                  >
                    -
                  </Button>
                  <span className="w-10 md:w-12 text-center font-semibold" data-testid="quantity-display">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 md:h-10 md:w-10 rounded-full"
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="increase-qty"
                  >
                    +
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  {product.inventory_quantity > 0 ? (
                    <>
                      <LowStockBadge quantity={product.inventory_quantity} />
                      {product.inventory_quantity > 5 && (
                        <p className="text-sm text-muted-foreground">{product.inventory_quantity} in stock</p>
                      )}
                    </>
                  ) : (
                    <LowStockBadge quantity={0} />
                  )}
                </div>
              </div>

              {/* Bundle Customization Form - Only for bundle products */}
              {product.category === 'bundles' && (
                <Card className="p-4 md:p-5 mb-6 bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-teal-800">
                    <Fish className="w-5 h-5" />
                    Tell Us About Your Tank
                  </h3>
                  <p className="text-sm text-teal-700 mb-4">
                    We'll hand-pick the perfect plants for your setup!
                  </p>
                  
                  {bundleFormError && (
                    <p className="text-sm text-red-600 mb-3 p-2 bg-red-50 rounded">{bundleFormError}</p>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="flex items-center gap-2 mb-1.5">
                        <Ruler className="w-4 h-4 text-teal-600" />
                        Tank Size (gallons) *
                      </Label>
                      <Input
                        type="number"
                        placeholder="e.g., 20, 55, 75..."
                        value={bundleForm.tank_size}
                        onChange={(e) => setBundleForm({...bundleForm, tank_size: e.target.value})}
                        className="bg-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="flex items-center gap-2 mb-1.5">
                        <Lightbulb className="w-4 h-4 text-teal-600" />
                        Lighting Level *
                      </Label>
                      <Select 
                        value={bundleForm.lighting_level} 
                        onValueChange={(v) => setBundleForm({...bundleForm, lighting_level: v})}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select your lighting..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low (basic LED/no light)</SelectItem>
                          <SelectItem value="medium">Medium (standard aquarium light)</SelectItem>
                          <SelectItem value="high">High (planted tank light)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="flex items-center gap-2 mb-1.5">
                        <Fish className="w-4 h-4 text-teal-600" />
                        Tank Inhabitants *
                      </Label>
                      <Select 
                        value={bundleForm.tank_inhabitants} 
                        onValueChange={(v) => setBundleForm({...bundleForm, tank_inhabitants: v})}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="What lives in your tank?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="community">Community fish (tetras, guppies, etc.)</SelectItem>
                          <SelectItem value="shrimp">Shrimp only</SelectItem>
                          <SelectItem value="shrimp_fish">Shrimp & small fish</SelectItem>
                          <SelectItem value="cichlids">Cichlids</SelectItem>
                          <SelectItem value="goldfish">Goldfish</SelectItem>
                          <SelectItem value="betta">Betta</SelectItem>
                          <SelectItem value="empty">Empty / New tank</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="mb-1.5 block">Additional Notes (optional)</Label>
                      <Textarea
                        placeholder="Any preferences, allergies in tank, or special requests..."
                        value={bundleForm.additional_notes}
                        onChange={(e) => setBundleForm({...bundleForm, additional_notes: e.target.value})}
                        className="bg-white resize-none"
                        rows={2}
                      />
                    </div>
                  </div>
                </Card>
              )}

              <Button
                onClick={handleAddToCart}
                disabled={product.inventory_quantity === 0}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 md:h-14 rounded-full font-medium text-base md:text-lg"
                data-testid="add-to-cart-detail-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart - ${(displayPrice * quantity).toFixed(2)}
              </Button>
            </div>
          </div>
          
          {/* Reviews Section */}
          <div className="mt-12 md:mt-16">
            <ProductReviews productId={productId} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetail;
